import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import Layout from '@/components/Layout';
import MechanicActiveJobBanner from '@/components/MechanicActiveJobBanner';
import MechanicClockBar from '@/components/MechanicClockBar';
import MechanicProductivityTile from '@/components/MechanicProductivityTile';
import MechanicIncentiveTile from '@/components/MechanicIncentiveTile';
import ProductivityMatrix from '@/components/dashboard/ProductivityMatrix';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import QuickBookingModal from '@/components/QuickBookingModal';
import { toast } from 'sonner';
import {
  ClipboardList, Clock, CalendarOff, MessageSquare,
  TrendingUp, TrendingDown, ArrowRight,
  ChevronRight, ChevronLeft, CalendarPlus,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt2 = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const isoToLocal = (iso) => { try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }); } catch { return iso; } };

export default function MechanicDashboard() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [showHours, setShowHours] = useState(false);
  const [showBooking, setShowBooking] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/mechanic/dashboard?year=${year}&month=${month}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  // Auto-refresh every 30s so new SM-assigned jobs surface on the dashboard
  // without a manual refresh. Skipped when tab is hidden.
  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) fetchData();
    }, 30000);
    return () => clearInterval(id);
  }, [fetchData]);

  if (loading || !data) {
    return <Layout user={user}><div className="p-8 text-slate-500">Loading your dashboard…</div></Layout>;
  }

  const { productivity, incentive, jobs, leave, performance_review: pr, period } = data;

  const goPrev = () => { const d = new Date(year, month - 2, 1); setYear(d.getFullYear()); setMonth(d.getMonth() + 1); };
  const goNext = () => { const d = new Date(year, month, 1); setYear(d.getFullYear()); setMonth(d.getMonth() + 1); };

  return (
    <Layout user={user}>
      <div className="space-y-6 max-w-7xl" data-testid="mechanic-dashboard">
        {/* iter131 Phase 2 — Clock-In / Break / Clock-Off bar */}
        <MechanicClockBar />

        {/* iter134 — Weekly productivity snapshot (4 cards: today/yesterday/this week/last week) */}
        <MechanicProductivityTile />

        {/* iter161 — Incentive Bucket. Running balance of completed-vs-target
            since the workshop's reset date. Positive = green (going toward
            your bonus), negative = amber (never red, per motivation policy). */}
        <MechanicIncentiveTile />

        {/* Live running-clock banner — only renders when mechanic is clocked in */}
        <MechanicActiveJobBanner />

        {/* Hero greeting */}
        <Card className="p-6 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white border-0">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-blue-200 text-sm">Welcome back,</p>
              <h1 className="text-3xl font-black mt-1" data-testid="mechanic-greeting">{user?.name || 'Mechanic'}</h1>
              <p className="text-blue-200 text-sm mt-1">
                Here's your day — keep building those hours.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* iter234 — Mechanics can take walk-in/phone bookings from
                  their iPad. Phone-only customer matching; booking lands
                  at THIS mechanic's workshop. */}
              <Button
                onClick={() => setShowBooking(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-5"
                data-testid="mechanic-new-booking-btn"
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                New Booking
              </Button>
              <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5">
                <button onClick={goPrev} className="hover:bg-white/20 rounded-full p-1" data-testid="prev-month-btn">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-semibold w-32 text-center">{period.label}</span>
                <button onClick={goNext} className="hover:bg-white/20 rounded-full p-1" data-testid="next-month-btn">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </Card>

        {/* 5 Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Tile 1: Jobs */}
          <button
            onClick={() => navigate('/job-cards')}
            className="text-left rounded-xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 p-5 hover:shadow-lg hover:scale-[1.02] transition-all relative group"
            data-testid="tile-jobs"
          >
            <ClipboardList className="h-6 w-6 text-blue-600 mb-2" />
            <p className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">My Jobs</p>
            <p className="text-4xl font-black text-blue-900 mt-1">{jobs.in_progress + jobs.pending}</p>
            <p className="text-xs text-blue-700 mt-1">
              <strong>{jobs.in_progress}</strong> in progress · <strong>{jobs.pending}</strong> pending
            </p>
            <p className="text-[11px] text-emerald-700 mt-2">{jobs.completed_this_month} completed this month</p>
            {jobs.escalated > 0 && (
              <span className="absolute top-3 right-3 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                {jobs.escalated} escalated
              </span>
            )}
            <ArrowRight className="absolute bottom-3 right-3 h-4 w-4 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>

          {/* Tile 2: Hours This Month — always-on "done vs passed" metric.
              Replaces the old "Revenue Loss / Bonus" tile (which only fired
              when underperforming) with a continuously motivating progress
              indicator the mechanic can check at a glance. */}
          <button
            onClick={() => setShowHours(true)}
            className={`text-left rounded-xl border-2 p-5 hover:shadow-lg hover:scale-[1.02] transition-all relative group ${
              productivity.performance_pct_so_far >= 100
                ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50'
                : productivity.performance_pct_so_far >= 80
                  ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50'
                  : 'border-rose-300 bg-gradient-to-br from-rose-50 to-red-50'
            }`}
            data-testid="tile-hours-so-far"
          >
            <Clock className={`h-6 w-6 mb-2 ${
              productivity.performance_pct_so_far >= 100 ? 'text-emerald-600'
                : productivity.performance_pct_so_far >= 80 ? 'text-amber-600' : 'text-rose-600'
            }`} />
            <p className={`text-[10px] uppercase tracking-wider font-bold ${
              productivity.performance_pct_so_far >= 100 ? 'text-emerald-700'
                : productivity.performance_pct_so_far >= 80 ? 'text-amber-700' : 'text-rose-700'
            }`}>
              Hours So Far
            </p>
            <p className={`text-4xl font-black mt-1 ${
              productivity.performance_pct_so_far >= 100 ? 'text-emerald-900'
                : productivity.performance_pct_so_far >= 80 ? 'text-amber-900' : 'text-rose-900'
            }`}>
              <span data-testid="hours-done">{productivity.actual_hours}h</span>
              <span className="text-lg text-slate-500 font-semibold">
                {' '}/ {productivity.expected_hours_so_far}h
              </span>
            </p>
            <p className="text-xs mt-1 text-slate-700">
              <strong>{productivity.weekdays_passed}</strong> working day{productivity.weekdays_passed === 1 ? '' : 's'} passed
            </p>
            <p className={`text-[11px] mt-2 ${
              productivity.deficit_hours_so_far > 0 ? 'text-rose-700' : 'text-emerald-700'
            }`}>
              {productivity.deficit_hours_so_far > 0
                ? `${productivity.deficit_hours_so_far.toFixed(1)}h behind`
                : productivity.deficit_hours_so_far < 0
                  ? `+${Math.abs(productivity.deficit_hours_so_far).toFixed(1)}h ahead`
                  : 'right on target'}
            </p>
            {(incentive.cash_bonus_received_this_month > 0 || incentive.cash_bonus_received_ytd > 0) && (
              <div className="mt-3 pt-2 border-t border-emerald-200/60 text-[11px]">
                <p className="text-emerald-800">
                  Cash paid: <strong>{fmt2(incentive.cash_bonus_received_this_month)}</strong> this month · <strong>{fmt2(incentive.cash_bonus_received_ytd)}</strong> YTD
                </p>
              </div>
            )}
          </button>

          {/* Tile 3: Productivity */}
          <button
            onClick={() => setShowHours(true)}
            className={`text-left rounded-xl border-2 p-5 hover:shadow-lg hover:scale-[1.02] transition-all relative group ${
              productivity.performance_pct >= 100 ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-green-50'
              : productivity.performance_pct >= 85 ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50'
              : 'border-rose-300 bg-gradient-to-br from-rose-50 to-orange-50'
            }`}
            data-testid="tile-productivity"
          >
            {productivity.performance_pct >= 100 ? <TrendingUp className="h-6 w-6 text-emerald-600 mb-2" />
              : <TrendingDown className="h-6 w-6 text-rose-600 mb-2" />}
            <p className={`text-[10px] uppercase tracking-wider font-bold ${
              productivity.performance_pct >= 100 ? 'text-emerald-700'
              : productivity.performance_pct >= 85 ? 'text-amber-700' : 'text-rose-700'
            }`}>
              Productivity
            </p>
            <p className={`text-4xl font-black mt-1 ${
              productivity.performance_pct >= 100 ? 'text-emerald-900'
              : productivity.performance_pct >= 85 ? 'text-amber-900' : 'text-rose-900'
            }`}>
              {productivity.performance_pct.toFixed(0)}%
            </p>
            <p className="text-xs mt-1 text-slate-700">
              <strong>{productivity.actual_hours}h</strong> / {productivity.expected_hours}h
            </p>
            <p className={`text-[11px] mt-2 ${
              productivity.deficit_hours > 0 ? 'text-rose-700' : 'text-emerald-700'
            }`}>
              {productivity.deficit_hours > 0
                ? `${productivity.deficit_hours.toFixed(1)}h down`
                : `+${Math.abs(productivity.deficit_hours).toFixed(1)}h ahead`}
            </p>
            <p className="text-[10px] text-slate-500 mt-1">tap for daily breakdown</p>
          </button>

          {/* Tile 4: Leave */}
          <button
            onClick={() => navigate('/leave')}
            className="text-left rounded-xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-5 hover:shadow-lg hover:scale-[1.02] transition-all"
            data-testid="tile-leave"
          >
            <CalendarOff className="h-6 w-6 text-purple-600 mb-2" />
            <p className="text-[10px] uppercase tracking-wider text-purple-700 font-bold">Leave Balance</p>
            <p className="text-4xl font-black text-purple-900 mt-1">{leave.annual_remaining.toFixed(0)}<span className="text-lg">h</span></p>
            <p className="text-xs text-purple-800 mt-1">annual remaining</p>
            <div className="text-[11px] text-purple-700 mt-2 space-y-0.5">
              <p>Sick: <strong>{leave.sick_remaining.toFixed(0)}h</strong>{leave.long_service_remaining > 0 ? <> · LSL: <strong>{leave.long_service_remaining.toFixed(0)}h</strong></> : null}</p>
              {leave.pending_count > 0 && <p className="text-amber-700">⏳ {leave.pending_count} pending request</p>}
            </div>
          </button>

          {/* Tile 5: Performance Review */}
          <button
            onClick={() => navigate('/performance-review')}
            className="text-left rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 p-5 hover:shadow-lg hover:scale-[1.02] transition-all"
            data-testid="tile-review"
          >
            <MessageSquare className="h-6 w-6 text-indigo-600 mb-2" />
            <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Performance Review</p>
            <p className="text-2xl font-black text-indigo-900 mt-1">{pr.label}</p>
            <p className="text-xs text-indigo-800 mt-1">
              {pr.exists ? (
                pr.status === 'draft' ? 'Continue your review' :
                pr.status === 'submitted' ? 'Awaiting manager' :
                pr.status === 'reviewed' ? '✓ Manager added comments' :
                pr.status === 'closed' ? '✓ Review closed' : pr.status
              ) : 'Start your quarterly review'}
            </p>
            <p className="text-[11px] text-indigo-700 mt-2">{pr.exists ? 'Tap to continue →' : 'Tap to start →'}</p>
          </button>
        </div>

        {/* iter131 Phase 3 — Productivity Matrix (privacy-gated: mechanic sees own row only) */}
        <ProductivityMatrix />

        {/* Per-day breakdown modal */}
        <DailyHoursModal
          open={showHours}
          onClose={() => setShowHours(false)}
          productivity={productivity}
          period={period}
        />

        {/* iter234 — Mechanic walk-in / phone booking. Phone-only customer
            matching; booking is created at this mechanic's own workshop. */}
        <QuickBookingModal
          open={showBooking}
          onClose={() => setShowBooking(false)}
          onCreated={(b) => toast.success(`Booked in — ${b?.booking_number || 'done'}`)}
          hideSearch
        />
      </div>
    </Layout>
  );
}

function DailyHoursModal({ open, onClose, productivity, period }) {
  if (!open) return null;
  const days = [];
  const last = new Date(period.year, period.month, 0).getDate();
  for (let d = 1; d <= last; d++) {
    const iso = `${period.year}-${String(period.month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const cell = productivity.daily?.[iso];
    const dt = new Date(period.year, period.month - 1, d);
    const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
    const expected = isWeekend ? 0 : 7.6;
    const hours = cell ? cell.hours : 0;
    days.push({ day: d, iso, isWeekend, hours, expected, source: cell?.source, dayName: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][dt.getDay()] });
  }
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="my-hours-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-indigo-600" />
            My Hours · {period.label}
          </DialogTitle>
          <DialogDescription>
            {productivity.actual_hours}h of {productivity.expected_hours}h target ({productivity.performance_pct.toFixed(0)}%).
            Weekends are shaded grey.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Day</th>
                <th className="p-2 text-right">Expected</th>
                <th className="p-2 text-right">Worked</th>
                <th className="p-2 text-right">Variance</th>
              </tr>
            </thead>
            <tbody>
              {days.map(d => {
                const variance = d.hours - d.expected;
                return (
                  <tr key={d.iso} className={`border-t ${d.isWeekend ? 'bg-slate-50 text-slate-400' : ''}`}>
                    <td className="p-2 tabular-nums">{isoToLocal(d.iso)}</td>
                    <td className="p-2">{d.dayName}</td>
                    <td className="p-2 text-right tabular-nums">{d.expected ? d.expected.toFixed(1) + 'h' : '—'}</td>
                    <td className={`p-2 text-right tabular-nums font-medium ${d.hours === 0 && !d.isWeekend ? 'text-rose-600' : ''}`}>
                      {d.hours > 0 ? d.hours.toFixed(1) + 'h' : (d.isWeekend ? '—' : '0h')}
                    </td>
                    <td className={`p-2 text-right tabular-nums ${variance > 0 ? 'text-emerald-700' : variance < 0 && !d.isWeekend ? 'text-rose-700' : 'text-slate-500'}`}>
                      {(d.hours > 0 || !d.isWeekend) ? (variance > 0 ? '+' : '') + variance.toFixed(1) + 'h' : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="close-hours-modal-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
